import type {
    ProviderConfig,
    RetryConfig,
    RouteConfiguration,
    TIFSession,
    UpdateRouteRequest,
} from '@thinkalpha/locates-ws-client';
import { ExecutionStyle, TIF } from '@thinkalpha/locates-ws-client';
import type { Route } from 'src/admin/LAB/contracts/routes';
import { z } from 'zod';

/** `tifKind` value for "no time in force". */
export const TIF_NONE = 'none' as const;
export type TifKind = typeof TIF_NONE | TIF;
export type TifSession = TIFSession['session'];

/** Matches the locates-ws route-creation default of 10 minutes, in the contract's seconds. */
export const DEFAULT_TIMEOUT_SECONDS = 600;
/** Bid priority for a newly targeted provider; the backend requires a value per provider. */
export const DEFAULT_BID_PRIORITY = 0;

/** A targeted provider while being edited; `null` priority is an empty input, blocked by validation. */
export interface DraftProviderTarget {
    providerId: string;
    bidPriority: number | null;
}

/**
 * Editable route fields, flattened for the form. Numeric fields are `null` while the input is
 * empty so an in-progress edit is representable; validation decides what may be saved.
 */
export interface RouteDraft {
    name: string;
    privateName: string;
    description: string;
    minFillPercent: number | null;
    minCompletion: number | null;
    /** `null` means "all". */
    resultsToShow: number | null;
    sortOrder: number | null;
    timeout: number | null;
    executionStyle: ExecutionStyle;
    tifKind: TifKind;
    tifIntervalSeconds: number | null;
    tifMaxRetries: number | null;
    tifMaxLifetimeSeconds: number | null;
    tifSession: TifSession | null;
    providers: DraftProviderTarget[];
}

export type RouteDraftField = Exclude<keyof RouteDraft, 'providers'>;

export interface LabRouteEditorState {
    firmId: string | undefined;
    routeId: string | null;
    /** Draft derived from the last server copy of the route; `null` until it loads. */
    baseline: RouteDraft | null;
    /** The user's working copy; `null` until the route loads. */
    draft: RouteDraft | null;
    providerSearch: string;
    isSaving: boolean;
    canManage: boolean;
}

export const initialLabRouteEditorState: LabRouteEditorState = {
    firmId: undefined,
    routeId: null,
    baseline: null,
    draft: null,
    providerSearch: '',
    isSaving: false,
    canManage: false,
};

export function draftFromRoute(route: Route): RouteDraft {
    const configuration = route.configuration;
    const tif = configuration?.timeInForce ?? null;
    return {
        name: route.name,
        privateName: route.privateName ?? '',
        description: route.description ?? '',
        minFillPercent: configuration?.executionConfig?.minFillPercent ?? null,
        minCompletion: configuration?.executionConfig?.minCompletion ?? null,
        resultsToShow: configuration?.resultsToShow ?? null,
        sortOrder: configuration?.sortOrder ?? 0,
        timeout: configuration?.timeout ?? DEFAULT_TIMEOUT_SECONDS,
        executionStyle: configuration?.executionStyle ?? ExecutionStyle.Auction,
        tifKind: tif?.kind ?? TIF_NONE,
        tifIntervalSeconds: tif ? tif.interval : null,
        tifMaxRetries: tif?.kind === TIF.Retry ? tif.maxRetries : null,
        tifMaxLifetimeSeconds: tif?.kind === TIF.Lifetime ? tif.maxLifetime : null,
        tifSession: tif?.kind === TIF.Session ? tif.session : null,
        providers: route.providers.map(({ providerId, bidPriority }) => ({ providerId, bidPriority })),
    };
}

/** Order-insensitive equality of provider target lists. */
export function sameDraftProviders(a: readonly DraftProviderTarget[], b: readonly DraftProviderTarget[]): boolean {
    if (a.length !== b.length) return false;
    const bidById = new Map(b.map(({ providerId, bidPriority }) => [providerId, bidPriority] as const));
    return a.every(({ providerId, bidPriority }) => bidById.has(providerId) && bidById.get(providerId) === bidPriority);
}

export function draftsEqual(a: RouteDraft, b: RouteDraft): boolean {
    if (a === b) return true;
    const { providers: aProviders, ...aFields } = a;
    const { providers: bProviders, ...bFields } = b;
    const keys = Object.keys(aFields) as RouteDraftField[];
    return keys.every((key) => aFields[key] === bFields[key]) && sameDraftProviders(aProviders, bProviders);
}

// Mirrors the locates-ws-client `RouteConfiguration` / `UpdateRouteRequest` constraints on the
// values that are present; which fields are required is decided in `validateRouteDraft` so a
// missing value never masks a range issue elsewhere.
const wholeNumber = z.number().int('Must be a whole number');
const RouteDraftSchema = z.object({
    name: z.string().trim().min(1, 'Required'),
    privateName: z.string(),
    description: z.string(),
    minFillPercent: z.number().min(0, 'Must be between 0 and 1').max(1, 'Must be between 0 and 1').nullable(),
    minCompletion: wholeNumber.min(0, 'Must be 0 or more').nullable(),
    resultsToShow: wholeNumber.min(1, 'Must be 1 or more').nullable(),
    sortOrder: wholeNumber.nullable(),
    timeout: wholeNumber.min(0, 'Must be 0 or more').nullable(),
    executionStyle: z.nativeEnum(ExecutionStyle),
    tifKind: z.union([z.literal(TIF_NONE), z.nativeEnum(TIF)]),
    tifIntervalSeconds: wholeNumber.min(0, 'Must be 0 or more').nullable(),
    tifMaxRetries: wholeNumber.min(0, 'Must be 0 or more').nullable(),
    tifMaxLifetimeSeconds: wholeNumber.min(0, 'Must be 0 or more').nullable(),
    tifSession: z.enum(['current', 'pre', 'regular', 'post']).nullable(),
    providers: z.array(z.object({ providerId: z.string(), bidPriority: wholeNumber.nullable() })),
});

/** Fields that must hold a value for the given draft; the rest may be empty. */
function requiredFields(draft: RouteDraft): RouteDraftField[] {
    const required: RouteDraftField[] = ['sortOrder', 'timeout'];
    if (draft.tifKind !== TIF_NONE) required.push('tifIntervalSeconds');
    if (draft.tifKind === TIF.Retry) required.push('tifMaxRetries');
    if (draft.tifKind === TIF.Lifetime) required.push('tifMaxLifetimeSeconds');
    if (draft.tifKind === TIF.Session) required.push('tifSession');
    return required;
}

/** Validation messages keyed by form field; provider issues are keyed by provider id. */
export interface RouteDraftIssues {
    fields: Partial<Record<RouteDraftField, string>>;
    providers: Record<string, string>;
}

export const NO_ISSUES: RouteDraftIssues = { fields: {}, providers: {} };

export function validateRouteDraft(draft: RouteDraft): RouteDraftIssues {
    const issues: RouteDraftIssues = { fields: {}, providers: {} };
    const result = RouteDraftSchema.safeParse(draft);
    if (!result.success) {
        for (const issue of result.error.issues) {
            const [head, index] = issue.path;
            if (head === 'providers') {
                const providerId = typeof index === 'number' ? draft.providers[index]?.providerId : undefined;
                if (providerId !== undefined && !(providerId in issues.providers)) {
                    issues.providers[providerId] = issue.message;
                }
            } else if (typeof head === 'string' && !(head in issues.fields)) {
                issues.fields[head as RouteDraftField] = issue.message;
            }
        }
    }
    for (const field of requiredFields(draft)) {
        if (draft[field] === null && !(field in issues.fields)) issues.fields[field] = 'Required';
    }
    for (const { providerId, bidPriority } of draft.providers) {
        if (bidPriority === null && !(providerId in issues.providers)) issues.providers[providerId] = 'Required';
    }
    return hasIssues(issues) ? issues : NO_ISSUES;
}

export function hasIssues(issues: RouteDraftIssues): boolean {
    return Object.keys(issues.fields).length > 0 || Object.keys(issues.providers).length > 0;
}

/** Assemble the wire request from a draft that passed {@link validateRouteDraft}. */
export function draftToUpdateRequest(draft: RouteDraft): UpdateRouteRequest {
    const interval = draft.tifIntervalSeconds ?? 0;
    const timeInForce = ((): RetryConfig | null => {
        switch (draft.tifKind) {
            case TIF.Retry:
                return { kind: TIF.Retry, interval, maxRetries: draft.tifMaxRetries ?? 0 };
            case TIF.Lifetime:
                return { kind: TIF.Lifetime, interval, maxLifetime: draft.tifMaxLifetimeSeconds ?? 0 };
            case TIF.Session:
                return { kind: TIF.Session, interval, session: draft.tifSession ?? 'current' };
            default:
                return null;
        }
    })();
    const configuration: RouteConfiguration = {
        timeInForce,
        resultsToShow: draft.resultsToShow,
        timeout: draft.timeout ?? DEFAULT_TIMEOUT_SECONDS,
        sortOrder: draft.sortOrder ?? 0,
        executionStyle: draft.executionStyle,
        executionConfig: { minFillPercent: draft.minFillPercent, minCompletion: draft.minCompletion },
    };
    const providers: ProviderConfig[] = draft.providers.map(({ providerId, bidPriority }) => ({
        providerId,
        bidPriority: bidPriority ?? DEFAULT_BID_PRIORITY,
    }));
    return {
        name: draft.name.trim(),
        privateName: draft.privateName,
        description: draft.description,
        providers,
        configuration,
    };
}
